import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { GetStaticProps } from 'next';
import SbEditable from 'storyblok-react';

import { Seo, Container, HeaderCta, Heading } from '~/components';
import { BannerDynamic } from '~/components/Banners/BannerDynamic';
import { Faq } from '~/components/Blocks/Faq';
import { Gallery, VacanciesSection } from '~/components/Careers';
import { FeatureIcon } from '~/components/Molecules/FeatureIcon';
import { VacancypageItems } from '~/graphql/sdk';
import { enhancedStaticProps, withLayout } from '~/lib';

export interface CareerProps extends WithStoryProps {
  vacancies?: VacancypageItems;
}

const Careers = ({ story, vacancies }: CareerProps) => {
  const populatedVacancies = story?.content?.vacancies?.map((cat) => ({
    ...cat,
    jobs: cat?.jobs?.map((job) =>
      vacancies?.items?.find((vacancy) => vacancy?.uuid === job),
    ),
  }));

  return (
    <>
      <Seo story={story} />
      <Container
        first
        shape="yellow-xl"
        className="grid grid-cols-1 gap-y-20 mx-0 md:mx-auto px-0"
      >
        <HeaderCta
          content={story?.content}
          titleVariant="h2"
          buttonLink="#vacancies"
          className="md:px-14"
        />

        <Gallery content={story?.content?.gallery} />

        <div className="px-6 lg:px-14">
          <Heading as="h6" variant="h6" className="font-sans mb-2">
            {story?.content?.perks_kicker}
          </Heading>
          <Heading as="h3" variant="h3">
            {story?.content?.perks_title}
          </Heading>
          <div className="grid md:grid-cols-2 md:gap-x-6 gap-y-6 pt-8">
            {story?.content?.perks?.map((perk) => (
              <div
                key={perk?._uid}
                className="border rounded-2xl flex flex-col items-center text-center p-10"
              >
                <FeatureIcon type={perk?.icon} />
                <Heading variant="h5">{perk?.title}</Heading>
                <div className="text-lg mt-4 text-gray-700">
                  {perk?.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>

      <VacanciesSection
        kicker={story?.content?.vacancies_kicker}
        title={story?.content?.vacancies_title}
        vacancies={populatedVacancies}
        buttonText={story?.content?.apply_button}
      />

      <Container>
        <Faq
          kicker={story?.content?.faq_kicker}
          title={story?.content?.faq_title}
          questions={story?.content?.faq}
          className="md:mx-10"
        />
      </Container>

      {story?.content?.banner?.content && (
        <SbEditable content={story?.content?.banner?.content}>
          <div className="max-w-6xl mx-auto px-4 md:px-14">
            <BannerDynamic content={story.content.banner.content} />
          </div>
        </SbEditable>
      )}
      <div className="h-80 bg-yellow-50 relative bottom-40 z-0 -mb-40" />
    </>
  );
};

export default withLayout(withStory(Careers));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ sdk }) => {
    let story;
    let vacancies;
    let notFound = false;

    try {
      story = (await sdk.careersOverviewItem({ slug: 'careers' }))
        .CareersoverviewItem;
    } catch (e) {
      notFound = true;
    }

    try {
      vacancies = (await sdk.vacancyPageItems({ filterId: '' }))
        .VacancypageItems;
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
        vacancies,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);
